import React from "react";
import { useQuery } from "react-query";
import { useTooltip } from "src/hooks/useTooltip";

import { AssessmentClass } from "src/modules/fithub-shared/anthropometry/assessment";
import { bonesKeys } from "src/modules/fithub-shared/anthropometry/bones";
import { foldsKeys } from "src/modules/fithub-shared/anthropometry/folds";
import { perimKeys } from "src/modules/fithub-shared/anthropometry/perimetry";
import api from "src/rest/api";
import ComparisionGraph from "./comparision-graph";

export interface GraphData {
  fat: number;
  wastes: number;
  muscles: number;
  bones?: number;
}

const Table: React.FC<{
  assessments: AssessmentClass[];
}> = ({ assessments }) => {
  const ids = assessments.map((e) => e.id);

  const { getTriggerProps, tooltipNode } = useTooltip({
    pointerPosition: true,
  });

  const { data: rawData, isLoading } = useQuery(
    ["assm", ...ids],
    () =>
      api
        .post<AssessmentClass[]>("/assessment/get-array", { ids })
        .then((res) => res.data),
    {
      enabled: ids.length > 0,
    },
  );

  // Reorganiza os dados conforme a ordem dos ids
  const data = rawData
    ? ids
        .map((id) => rawData.find((a) => a.id === id))
        .filter((a): a is AssessmentClass => Boolean(a))
    : [];

  const renderSection = (
    title: string,
    keys: { name: string; pt: string }[],
    accessor: (a: AssessmentClass) => Record<string, any>,
  ) => (
    <div className="mb-2 flex-col">
      <table className="min-w-full divide-y divide-zinc-200 border dark:divide-zinc-700 dark:border-zinc-700">
        <thead>
          <tr>
            <th className="w-96 bg-zinc-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              {title}
            </th>
            {data.map((assessment, index) => (
              <th
                key={assessment.id}
                className="flex-1 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500 odd:bg-zinc-50 even:bg-zinc-100 dark:text-zinc-400 dark:odd:bg-zinc-800 dark:even:bg-zinc-750"
              >
                <div className="flex items-center justify-between">
                  <p>{assessment.title}</p>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 bg-white dark:divide-zinc-700 dark:bg-zinc-900">
          {keys.map((e) => (
            <tr
              key={e.name}
              aria-label={e.pt.toUpperCase()}
              {...getTriggerProps()}
              className="items-center transition-all duration-200"
            >
              <td className="w-96 whitespace-nowrap px-6 py-4 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {e.pt}
              </td>
              {data.map((assessment) => (
                <td
                  key={assessment.id}
                  className="flex-1 whitespace-nowrap px-6 py-4 text-sm text-zinc-500 odd:bg-zinc-50 even:bg-zinc-100 dark:text-zinc-300 dark:odd:bg-zinc-800 dark:even:bg-zinc-750"
                >
                  {accessor(assessment)?.[e.name] ?? "--"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-4 text-center text-sm text-zinc-500">
        Carregando avaliações...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-zinc-500">
        Nenhuma avaliação encontrada.
      </div>
    );
  }

  return (
    <div className="flex-1 flex-col p-4">
      <ComparisionGraph data={data} />
      {renderSection("Perimetria", perimKeys, (a) => a.content.perimetry)}
      {renderSection("Dobras Cutâneas", foldsKeys, (a) => a.content.folds)}
      {renderSection("Diâmetros Ósseos", bonesKeys, (a) => a.content.bones)}
      {tooltipNode}
    </div>
  );
};

export default Table;
